import { api } from "@/lib/api";

// Patient Documents uses the SAME existing endpoints as Lumina Documents.
// This wrapper adds the `status` list param (needed for status=failed) without
// touching luminaDocs.js. Everything else is re-exported from there.
export {
  uploadDocument, getJob, getAnalysis, getFileContent, assignPatient,
  searchPatients, fileToBase64, MAX_FILE_BYTES, ACCEPTED,
} from "@/lib/luminaDocs";

const B = "/api/edi/documents";

// client_id required. Optional: skip, limit, status, doc_type, and the two new axes
// link_status (auto_linked|new_patient|needs_review|confirmed), chart_status
// (ready|ingesting|ingested|awaiting|nothing|removed|failed), relevance, deleted (true).
export async function listAnalyses({ client_id, skip = 0, limit = 200, status, doc_type, link_status, chart_status, relevance, deleted, view, count_only, cursor, etag }) {
  const params = { client_id, skip, limit };
  if (status) params.status = status;
  if (doc_type) params.doc_type = doc_type;
  if (link_status) params.link_status = link_status;
  if (chart_status) params.chart_status = chart_status;
  if (relevance) params.relevance = relevance;
  if (deleted) params.deleted = true;
  if (view) params.view = view;
  if (count_only) params.count_only = true;
  if (cursor) params.cursor = cursor;
  const { data } = await api.get(`${B}/analyses`, { params, ...(etag ? { etag: true } : {}) });
  return data; // { total, analyses[], next_cursor? }
}

// Thin list (Delta-Sync §2a): small, display-ready rows — no OCR/extraction blob.
// Carries read_patient_name/dob, edi_client_match, service_date, sender, subject,
// summary_line + all volatile status fields + updated_at/state_version/content_version.
// Collection ETag → repeat identical reads 304 without the server decrypting a row.
export async function listAnalysesThin({ client_id, limit = 500, status, doc_type, deleted, count_only, updated_since, cursor, watchlist, with_comments, review_flag, etag }) {
  const params = { client_id, limit };
  if (status) params.status = status;
  if (doc_type) params.doc_type = doc_type;
  if (deleted) params.deleted = true;
  if (count_only) params.count_only = true;
  if (updated_since) params.updated_since = updated_since;
  if (cursor) params.cursor = cursor;
  // Documents add-on (2026-10-29): server-side, index-backed toggles. Omitting a param = OFF.
  if (watchlist) params.watchlist = true;
  if (with_comments) params.with_comments = true;
  if (review_flag) params.review_flag = review_flag;
  const { data } = await api.get(`${B}/analyses/thin`, { params, ...(etag ? { etag: true } : {}) });
  return data; // { total, rows[], next_cursor? }
}

// Delta feed (Delta-Sync §2a): only rows whose volatile status moved since `since`.
// Returns { changes[], count, now, next_since? }. Pass back the exact `now` next time.
export async function analysesChanges({ client_id, since }) {
  const params = { client_id };
  if (since) params.since = since;
  const { data } = await api.get(`${B}/analyses/changes`, { params });
  return data;
}

// Documents the pipeline couldn't read. Returns { total, analyses[] }.
export async function listFailed(client_id) {
  const { data } = await api.get(`${B}/analyses`, { params: { client_id, status: "failed", skip: 0, limit: 100 } });
  return data;
}

/* ─── Chart-data ingest workflow (per document) ─── */
export async function chartPreview(analysisId) {
  const { data } = await api.get(`${B}/analyses/${analysisId}/chart-preview`);
  return data;
}
export async function chartIngest(analysisId) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/chart-ingest`);
  return data; // 202 { status:"ingesting" }
}
export async function chartRemove(analysisId) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/chart-remove`);
  return data; // { chart_data_status:"removed" }
}
export async function chartRetry(analysisId) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/chart-retry`);
  return data; // 202 { status:"ingesting" }
}
export async function chartActivity(analysisId) {
  const { data } = await api.get(`${B}/analyses/${analysisId}/chart-activity`);
  return data; // { activity[] }
}
// Mark a document reviewed (append-once; first reviewer + timestamp win). Called on open.
export async function markReviewed(analysisId) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/reviewed`);
  return data; // { reviewed, already, edi_reviewed_at }
}

/* ─── Documents add-on (2026-10-29): comments · review flag · watchlist ───
   All document-scoped by analysis_id; 100% additive, never touch the list fast-path. */
// Comments are never destroyed — add or edit only; every edit keeps prior text in `history`.
export async function listComments(analysisId) {
  const { data } = await api.get(`${B}/analyses/${analysisId}/comments`);
  return data; // { comments[], total }
}
export async function addComment(analysisId, text) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/comments`, { text });
  return data; // the created comment (also bumps edi_comment_count on the row)
}
export async function editComment(analysisId, commentId, text) {
  const { data } = await api.patch(`${B}/analyses/${analysisId}/comments/${commentId}`, { text });
  return data; // the updated comment (prior text appended to history)
}
// Single-valued review flag: additional_review | abnormal | normal, or null/"" to clear.
export async function setReviewFlag(analysisId, flag) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/flag`, { flag: flag || null });
  return data; // { edi_review_flag, edi_review_flag_by, edi_review_flag_at }
}
export async function setWatchlist(analysisId, on) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/watchlist`, { on: !!on });
  return data; // { edi_watchlist, by, at }
}

/* ─── Group (bulk) actions ─── */
export async function bulkIngest(analysis_ids) {
  const { data } = await api.post(`${B}/chart-ingest`, { analysis_ids });
  return data; // { accepted[], skipped[{analysis_id,filename,reason}] }
}
// Selective chart workflow (2026-11): preview itemised chart data, then ingest only chosen iids.
export async function chartPreviewBulk(analysis_ids) {
  const { data } = await api.post(`${B}/chart-preview`, { analysis_ids });
  return data; // { previews[], skipped[], errors[], totals, summary, patients }
}
export async function chartIngestSelective(analysis_ids, selections) {
  const { data } = await api.post(`${B}/chart-ingest`, { analysis_ids, selections });
  return data; // 202 { accepted[], skipped[] }
}
export async function bulkDelete(client_id, analysis_ids) {
  const { data } = await api.post(`${B}/delete`, { client_id, analysis_ids });
  return data; // { deleted[], skipped[], chart_data_removed }
}
export async function bulkRestore(client_id, analysis_ids) {
  const { data } = await api.post(`${B}/restore`, { client_id, analysis_ids });
  return data; // { restored }
}

// ---- Release 17 Oct: Upload & Process with auto-separate (split) ----
export const SPLIT_MAX_BYTES = 50 * 1024 * 1024;
export async function uploadSplit({ client_id, file, base64_data, batch_id, onUploadProgress }) {
  const body = { client_id, filename: file.name, content_type: file.type || "application/pdf", base64_data };
  if (batch_id) body.batch_id = batch_id;
  const { data } = await api.post(`${B}/upload-split`, body, { onUploadProgress });
  return data; // 202 { job_id, status, filename, poll_url }
}
export async function getSplitJob(jobId) {
  const { data } = await api.get(`${B}/split-jobs/${jobId}`);
  return data; // { status, progress{stage,message,done,total}, result{mode,...}, error, analysis_id }
}
export async function listSplitBatches({ client_id, date_from, date_to, filename, etag }) {
  const params = { client_id };
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  if (filename) params.filename = filename;
  const { data } = await api.get(`${B}/split-batches`, { params, ...(etag ? { etag: true } : {}) });
  return Array.isArray(data?.batches) ? data.batches : [];
}
export async function retrySplitSegment(parentId, segment_index) {
  const { data } = await api.post(`${B}/split-batches/${parentId}/retry`, { segment_index });
  return data; // 202 { job_id }
}
export async function skipSplitSegment(parentId, segment_index) {
  const { data } = await api.post(`${B}/split-batches/${parentId}/skip`, { segment_index });
  return data;
}
export async function downloadSplitOriginal(parentId) {
  const { data } = await api.get(`${B}/split-files/${parentId}/original`);
  const bin = atob(data.file_content || "");
  const bytes = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: data.content_type || "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = data.filename || "original.pdf";
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1500);
}
